export interface IAiSpreadsheetCellAlignment {
  horizontal?: "left" | "center" | "right" | string | null;
  vertical?: "top" | "center" | "bottom" | string | null;
  wrap_text?: boolean | null;
}

export interface IAiSpreadsheetCellFormat {
  bold?: boolean | null;
  background_color?: string | null;
  bg_color?: string | null;
  fill_color?: string | null;
  font_color?: string | null;
  fg_color?: string | null;
  alignment?: IAiSpreadsheetCellAlignment | null;
  format_code?: string | null;
}

export interface IAiSpreadsheetCellError {
  code?: string | null;
  message?: string | null;
}

export interface IAiSpreadsheetCellSourceRef {
  job_id?: number | string | null;
  invoice_id?: number | string | null;
  sku?: string | null;
  commission_category?: string | null;
  technician_external_id?: string | number | null;
  [key: string]: unknown;
}

export const AI_SPREADSHEET_CELL_KIND = {
  formula: "formula",
  value: "value",
} as const;

export type AiSpreadsheetCellKind =
  (typeof AI_SPREADSHEET_CELL_KIND)[keyof typeof AI_SPREADSHEET_CELL_KIND];

export const AI_SPREADSHEET_OVERRIDE_OUTCOME = {
  autoAccepted: "auto_accepted",
  needsReview: "needs_review",
  unmapped: "unmapped",
} as const;

export type AiSpreadsheetOverrideOutcome =
  (typeof AI_SPREADSHEET_OVERRIDE_OUTCOME)[keyof typeof AI_SPREADSHEET_OVERRIDE_OUTCOME];

export const AI_SPREADSHEET_OVERRIDE_REVIEW_REASON = {
  drifted: "drifted",
  ambiguous: "ambiguous",
} as const;

export type AiSpreadsheetOverrideReviewReason =
  (typeof AI_SPREADSHEET_OVERRIDE_REVIEW_REASON)[keyof typeof AI_SPREADSHEET_OVERRIDE_REVIEW_REASON];

export const AI_SPREADSHEET_VALUE_TYPE = {
  number: "number",
  currency: "currency",
  percent: "percent",
  text: "text",
  date: "date",
  boolean: "boolean",
} as const;

export type AiSpreadsheetValueType =
  (typeof AI_SPREADSHEET_VALUE_TYPE)[keyof typeof AI_SPREADSHEET_VALUE_TYPE];

const AI_SPREADSHEET_NUMERIC_VALUE_TYPES = [
  AI_SPREADSHEET_VALUE_TYPE.number,
  AI_SPREADSHEET_VALUE_TYPE.currency,
  AI_SPREADSHEET_VALUE_TYPE.percent,
] as const;

export type AiSpreadsheetNumericValueType =
  (typeof AI_SPREADSHEET_NUMERIC_VALUE_TYPES)[number];

export const isAiSpreadsheetNumericValueType = (
  value: string | null | undefined,
): value is AiSpreadsheetNumericValueType =>
  value != null &&
  AI_SPREADSHEET_NUMERIC_VALUE_TYPES.includes(
    value as AiSpreadsheetNumericValueType,
  );

export const AI_SPREADSHEET_DEFAULT_FORMAT_CODE: Partial<
  Record<AiSpreadsheetValueType, string>
> = {
  [AI_SPREADSHEET_VALUE_TYPE.currency]: "$#,##0.00",
  [AI_SPREADSHEET_VALUE_TYPE.percent]: "0.00%",
  [AI_SPREADSHEET_VALUE_TYPE.date]: "yyyy-mm-dd",
};

export interface IAiSpreadsheetCellOverrideValue {
  raw_input?: string | null;
  kind?: string | null;
  value_type?: string | null;
  computed_value?: string | number | boolean | null;
}

export interface IAiSpreadsheetCellLocation {
  sheet_id: string;
  sheet_name: string | null;
  row: number;
  col: number;
  address: string;
}

export interface IAiSpreadsheetCellOverride {
  id: string;
  override_key: string;
  outcome: AiSpreadsheetOverrideOutcome | null;
  review_reason: AiSpreadsheetOverrideReviewReason | null;
  stale: boolean;
  stale_at: string | null;
  value: IAiSpreadsheetCellOverrideValue | null;
  previous_value: string | number | boolean | null;
  generated_value: string | number | boolean | null;
  edited_by: { id: string; full_name: string | null } | null;
  edited_at: string;
  superseded_at: string | null;
  current: boolean;
  cell?: IAiSpreadsheetCellLocation | null;
}

export interface IAiScriptEditedCellSelection {
  override: IAiSpreadsheetCellOverride;
  coordinate: string;
}

export interface IAiSpreadsheetCell {
  id: string;
  ai_spreadsheet_sheet_id: string;
  row: number;
  col: number;
  kind: string | null;
  raw_input: string | null;
  computed_value: string | number | boolean | null;
  value_type: string | null;
  format: IAiSpreadsheetCellFormat | null;
  error: string | IAiSpreadsheetCellError | null;
  accessor_key: string | null;
  source_ref: IAiSpreadsheetCellSourceRef | null;
  suggested_values: string[] | null;
  locked?: boolean | null;
  edited_by_admin_user_id: string | null;
  edited_at: string | null;
  override?: IAiSpreadsheetCellOverride | null;
  manual_row_id?: string | null;
  created_at: string;
  updated_at: string;
}
